import { createContext, ReactNode, useContext } from 'react';

/**
 * The header control gives the methods that can control different parts of the article card header
 */
interface HeaderControl {
  /**
   * The title within the article card header
   * @param _ The title to set
   */
  setTitle: (_: string) => void;
  /**
   *  The subtitle within the article card header
   * @param _ The subtitle to set
   */
  setSubtitle: (_: string) => void;
  /**
   * Set the html component for the title as <h2>, using <h1> for the subtitle
   * @param _ True, to use <h2> for the title, which will swap the <h1> for the subtitle
   */
  setUseTitleH2: (_: boolean) => void;
  /**
   * Reset the title to the default
   */
  resetTitle: () => void;
}

// For private use only
const HeaderControlContext = createContext<HeaderControl | undefined>(
  undefined
);

/**
 * Use the header control context
 */
export const useHeaderControlContext = () => {
  const headerControlContext = useContext(HeaderControlContext);
  if (!headerControlContext)
    throw new Error(
      'No HeaderControl initialized when calling useHeaderControlContext'
    );
  return headerControlContext;
};

/**
 * The properties for the header control context provider
 */
interface HeaderControlContextProviderProps extends HeaderControl {
  /**
   * The children of the provider
   */
  children: ReactNode;
}

/**
 * The header control context provider
 */
export const HeaderControlContextProvider = (
  props: HeaderControlContextProviderProps
) => {
  return (
    <HeaderControlContext.Provider value={props}>
      {props.children}
    </HeaderControlContext.Provider>
  );
};
